import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { MAX_APPLICATION_NAME_LENGTH } from '../constants';
import type { Application } from '../types/application';

const i18nBase = 'resources.applications';

export interface ApplicationFormDialogProps {
  record: Application | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ApplicationFormValues {
  name: string;
}

const emptyDefaults: ApplicationFormValues = { name: '' };

export const ApplicationFormDialog: React.FC<ApplicationFormDialogProps> = ({
  record,
  open,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [submitting, setSubmitting] = useState(false);

  const isEdit = record !== null;

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<ApplicationFormValues>({
    defaultValues: emptyDefaults,
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!open) return;
    reset(record ? { name: record.name ?? '' } : emptyDefaults);
  }, [open, record, reset]);

  const onSubmit = async (values: ApplicationFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      if (isEdit && record) {
        await dataProvider.update('applications', {
          id: record.id,
          data: { name: values.name },
          previousData: record,
        });
        notify(`${i18nBase}.notifications.updated`, { type: 'success' });
      } else {
        await dataProvider.create('applications', {
          data: { name: values.name },
        });
        notify(`${i18nBase}.notifications.created`, { type: 'success' });
      }
      onSuccess();
    } catch (err: unknown) {
      const failKey = isEdit
        ? `${i18nBase}.notifications.updateFailed`
        : `${i18nBase}.notifications.createFailed`;
      notify(extractErrorMessage(err, failKey, translate), { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(`${i18nBase}.dialog.title`)}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 4 } }}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: translate(`${i18nBase}.validation.nameRequired`),
              maxLength: {
                value: MAX_APPLICATION_NAME_LENGTH,
                message: translate(`${i18nBase}.validation.nameMaxLength`, {
                  max: MAX_APPLICATION_NAME_LENGTH,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                onBlur={() => {
                  field.onBlur();
                  void trigger('name');
                }}
                fullWidth
                required
                autoFocus
                label={translate(`${i18nBase}.fields.name`)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: MAX_APPLICATION_NAME_LENGTH }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate(`${i18nBase}.actions.discard`)}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !isValid}
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate(`${i18nBase}.actions.submit`)}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ApplicationFormDialog;
